import { useState } from 'react';
import { api } from '../lib/api';
import { MONTHS, MONTH_FULL } from '../lib/format';
import { Toast } from '../ui/common';

export default function Setup({ config, onSaved }) {
  const [toast, setToast] = useState('');
  const done = (msg) => { setToast(msg); onSaved && onSaved(); };
  return (
    <div>
      {toast && (
        <Toast message={toast} onDone={() => setToast('')} />
      )}

      <div className="cm-grid-2">
        <CurrencyCard config={config} onDone={done} />
        <StartCard config={config} onDone={done} />
      </div>

      <ClientCard kind="income" title="Income Client Categories" config={config} onDone={done} />
      <ClientCard kind="expense" title="Expense Vendor Categories" config={config} onDone={done} />
      <AccountsCard config={config} onDone={done} />
      <GoalsCard config={config} onDone={done} />
    </div>
  );
}

function useSave() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const run = async (fn, onOk) => {
    setBusy(true); setError(null);
    try { await fn(); onOk(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  return { busy, error, run };
}

function CurrencyCard({ config, onDone }) {
  const [v, setV] = useState(config.currency || '₹');
  const { busy, error, run } = useSave();
  return (
    <div className="cm-form-section-card">
      <div className="cm-form-section-title">System Currency Symbol</div>
      {error && <div className="err">{error}</div>}
      <div className="cm-field-group">
        <input value={v} onChange={(e) => setV(e.target.value)} maxLength={4} placeholder="e.g. ₹ or $" />
      </div>
      <button className="cm-btn-emerald" disabled={busy} onClick={() => run(() => api.put('/api/setup/currency', { currency: v }), () => onDone('Currency saved successfully'))}>
        Save Currency
      </button>
    </div>
  );
}

function StartCard({ config, onDone }) {
  const [month, setMonth] = useState(config.startMonth || 'January');
  const [year, setYear] = useState(config.startYear || new Date().getFullYear());
  const { busy, error, run } = useSave();
  return (
    <div className="cm-form-section-card">
      <div className="cm-form-section-title">Accounting Start Period</div>
      {error && <div className="err">{error}</div>}
      <div className="cm-grid-2">
        <div className="cm-field-group">
          <label>Start Month</label>
          <select value={month} onChange={(e) => setMonth(e.target.value)}>
            {MONTH_FULL.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="cm-field-group">
          <label>Start Year</label>
          <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} />
        </div>
      </div>
      <button className="cm-btn-emerald" disabled={busy} onClick={() => run(() => api.put('/api/setup/start', { month, year }), () => onDone('Start period saved successfully'))}>
        Save Start Period
      </button>
    </div>
  );
}

function ClientCard({ kind, title, config, onDone }) {
  const initialClients = (config.clients?.[kind] || []).map((c) => ({
    name: c.name || c,
    status: c.status || 'Active',
  }));
  if (initialClients.length === 0) {
    const categoriesList = config.categories?.[kind] || [];
    categoriesList.forEach((cat) => initialClients.push({ name: cat, status: 'Active' }));
  }

  const [list, setList] = useState(initialClients);
  const [search, setSearch] = useState('');
  const { busy, error, run } = useSave();

  const updName = (i, name) => setList((p) => p.map((x, j) => (j === i ? { ...x, name } : x)));
  const toggleStatus = (i) => setList((p) => p.map((x, j) => (j === i ? { ...x, status: x.status === 'Active' ? 'Inactive' : 'Active' } : x)));

  const filteredIndices = list
    .map((c, i) => ({ c, i }))
    .filter(({ c }) => !search.trim() || c.name.toLowerCase().includes(search.trim().toLowerCase()));

  const saveClients = () => {
    const cleaned = list.filter((c) => c.name.trim().length > 0);
    return api.put('/api/setup/clients', { kind, clients: cleaned });
  };

  return (
    <div className="cm-form-section-card">
      <div className="cm-form-section-title">{title} ({list.length})</div>
      {error && <div className="err">{error}</div>}

      <div className="cm-field-group" style={{ marginBottom: 16 }}>
        <input
          placeholder="Filter categories…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
        {filteredIndices.map(({ c, i }) => (
          <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input
              value={c.name}
              onChange={(e) => updName(i, e.target.value)}
              style={{ opacity: c.status === 'Inactive' ? 0.6 : 1 }}
            />
            <button
              type="button"
              className={`cm-badge ${c.status === 'Active' ? 'active-client' : 'inactive-client'}`}
              style={{ cursor: 'pointer', minWidth: 80, justifyContent: 'center' }}
              onClick={() => toggleStatus(i)}
            >
              {c.status}
            </button>
            <button
              type="button"
              style={{ background: 'none', border: 'none', color: 'var(--ink)', cursor: 'pointer', fontSize: '1.1rem' }}
              onClick={() => setList((p) => p.filter((_, j) => j !== i))}
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="cm-btn-outline"
          onClick={() => setList((p) => [...p, { name: '', status: 'Active' }])}
        >
          ＋ Add Category Item
        </button>
        <button
          type="button"
          className="cm-btn-emerald"
          disabled={busy}
          onClick={() => run(saveClients, () => onDone(`${title} saved successfully`))}
        >
          Save {title}
        </button>
      </div>
    </div>
  );
}

function AccountsCard({ config, onDone }) {
  const [list, setList] = useState((config.accounts || []).map((a) => ({ name: a.name, beginningBalance: a.beginningBalance || 0 })));
  const { busy, error, run } = useSave();
  const upd = (i, k, val) => setList((p) => p.map((x, j) => (j === i ? { ...x, [k]: val } : x)));
  return (
    <div className="cm-form-section-card">
      <div className="cm-form-section-title">Bank &amp; Cash Accounts Setup</div>
      {error && <div className="err">{error}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
        {list.map((a, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 12, alignItems: 'center' }}>
            <input placeholder="Account Name" value={a.name} onChange={(e) => upd(i, 'name', e.target.value)} />
            <input type="number" placeholder="Beginning Balance" value={a.beginningBalance} onChange={(e) => upd(i, 'beginningBalance', Number(e.target.value))} />
            <button type="button" style={{ background: 'none', border: 'none', color: 'var(--ink)', cursor: 'pointer', fontSize: '1.1rem' }} onClick={() => setList((p) => p.filter((_, j) => j !== i))}>✕</button>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button type="button" className="cm-btn-outline" onClick={() => setList((p) => [...p, { name: '', beginningBalance: 0 }])}>
          ＋ Add Account
        </button>
        <button type="button" className="cm-btn-emerald" disabled={busy} onClick={() => run(() => api.put('/api/setup/accounts', { accounts: list.filter((a) => a.name.trim()) }), () => onDone('Accounts saved successfully'))}>
          Save Accounts
        </button>
      </div>
    </div>
  );
}

function GoalsCard({ config, onDone }) {
  const years = Object.keys(config.goals || {});
  const [year, setYear] = useState(years[0] || String(new Date().getFullYear()));
  const [months, setMonths] = useState(() => ({ ...(config.goals?.[years[0]] || {}) }));
  const { busy, error, run } = useSave();
  const pickYear = (y) => { setYear(y); setMonths({ ...(config.goals?.[y] || {}) }); };
  const upd = (m, val) => setMonths((p) => ({ ...p, [m]: val === '' ? null : Number(val) }));
  if (years.length === 0) return null;
  return (
    <div className="cm-form-section-card">
      <div className="cm-form-section-title">Monthly Profit Goals Setup</div>
      {error && <div className="err">{error}</div>}

      <div className="cm-field-group" style={{ maxWidth: 200, marginBottom: 16 }}>
        <label>Goal Year</label>
        <select value={year} onChange={(e) => pickYear(e.target.value)}>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      <div className="cm-dates-grid-desktop" style={{ marginBottom: 16 }}>
        {MONTHS.map((m) => (
          <div className="cm-field-group" key={m} style={{ marginBottom: 0 }}>
            <label>{m} Goal (₹)</label>
            <input type="number" value={months[m] ?? ''} onChange={(e) => upd(m, e.target.value)} />
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" className="cm-btn-emerald" disabled={busy} onClick={() => run(() => api.put('/api/setup/goals', { year: Number(year), months }), () => onDone('Profit goals saved successfully'))}>
          Save Profit Goals
        </button>
      </div>
    </div>
  );
}
